import { FacilityAlternateIdKnownKind } from "../facility/alternateId";
import { hasValue } from "../utils/hasValue";
import { findAlternateId, findAlternateIds, isAlternateIdExpired } from "./findAlternateId";

type AlternateIdLike = {
  type?: string | null;
  value?: string | null;
  hasNoId?: boolean | null;
  expiresAt?: Date | null;
};

export type HmbpEpaIdResolution = {
  /** Trimmed values of the facility's active EPA IDs. */
  activeEpaIdValues: string[];
  /** The EPA ID to report, or undefined when the facility has none. */
  epaId: string | undefined;
  /** Multiple active EPA IDs with no valid selection — consumers must not guess which to report. */
  needsSelection: boolean;
};

/**
 * Resolves which of a facility's EPA IDs an HMBP reports. The `hmbpEpaId` CA
 * state field records the report's choice; it wins when it still matches an
 * active EPA ID, otherwise the facility's default EPA ID is used.
 */
export function resolveHmbpEpaId(
  alternateIds: ReadonlyArray<AlternateIdLike | null | undefined> | null | undefined,
  hmbpEpaId: string | null | undefined,
  asOf: Date = new Date()
): HmbpEpaIdResolution {
  const epaRows = (alternateIds ?? []).filter(hasValue).flatMap((id) => {
    const value = id.value?.trim();
    if (id.type !== FacilityAlternateIdKnownKind.EPA || !value) {
      return [];
    }
    return [
      {
        type: id.type,
        value,
        hasNoId: id.hasNoId ?? false,
        expiresAt: id.expiresAt ?? null,
      },
    ];
  });

  const activeEpaIdValues = findAlternateIds(epaRows, [FacilityAlternateIdKnownKind.EPA])
    .filter((row) => !isAlternateIdExpired(row, asOf))
    .map((row) => row.value);

  const selected = hmbpEpaId?.trim();
  const selectionIsActive = !!selected && activeEpaIdValues.includes(selected);

  return {
    activeEpaIdValues,
    epaId: selectionIsActive
      ? selected
      : findAlternateId(epaRows, [FacilityAlternateIdKnownKind.EPA])?.value,
    needsSelection: activeEpaIdValues.length > 1 && !selectionIsActive,
  };
}
